import { useState, useRef, useEffect, useCallback } from "react";

let _idCounter = 1;
let _textFieldCounter = 1;
const uid = () => `el_${_idCounter++}`;

function syncIdCounter(elements = []) {
    let maxId = 0;
    elements.forEach((el) => {
        const match = /^el_(\d+)$/.exec(String(el?.id || ""));
        if (!match) return;
        maxId = Math.max(maxId, Number(match[1]));
    });
    if (maxId >= _idCounter) {
        _idCounter = maxId + 1;
    }
}

function withUniqueElementIds(elements = []) {
    const used = new Set();
    const normalized = elements.map((el) => {
        let id = typeof el?.id === "string" ? el.id.trim() : "";
        if (!id || used.has(id)) {
            id = uid();
            while (used.has(id)) id = uid();
        }
        used.add(id);
        return id === el.id ? el : { ...el, id };
    });

    syncIdCounter(normalized);
    return normalized;
}

function createDefaultModelElements(canvasSize) {
    return [{
        id: uid(),
        type: "image",
        x: 0,
        y: 0,
        width: canvasSize.width,
        height: canvasSize.height,
        src: "",
        imageUrl: "",
        opacity: 1,
        zIndex: 0,
        fieldName: "background",
        fixed: false,
    }];
}

function normalizeInitialElements(mode, initialElements, initialCanvasSize) {
    if (Array.isArray(initialElements) && initialElements.length > 0) {
        return withUniqueElementIds(initialElements);
    }
    if (mode === "model") {
        const defaults = createDefaultModelElements(initialCanvasSize);
        syncIdCounter(defaults);
        return defaults;
    }
    syncIdCounter([]);
    return [];
}

export default function useEditorState({
    mode = "design",
    initialTitle = "Sans titre",
    initialElements = [],
    initialCanvasBg = "#ffffff",
    initialCanvasSize = { width: 1080, height: 1080 },
} = {}) {
    const [title, setTitle] = useState(initialTitle);
    const [elements, setElements] = useState(() => normalizeInitialElements(mode, initialElements, initialCanvasSize));
    const [selectedId, setSelectedId] = useState(null);
    const [canvasBg, setCanvasBgState] = useState(initialCanvasBg);
    const [canvasSize, setCanvasSizeState] = useState(initialCanvasSize);

    // ── History (undo/redo) ───────────────────────────────────────────────────
    const [past, setPast] = useState([]);
    const [future, setFuture] = useState([]);
    const canUndo = past.length > 0;
    const canRedo = future.length > 0;

    const fileInputRef = useRef(null);
    const dragRef = useRef(null);
    const resizeRef = useRef(null);
    const scaleRef = useRef(1);

    const elementsRef = useRef(elements);
    useEffect(() => { elementsRef.current = elements; }, [elements]);

    const selectedIdRef = useRef(selectedId);
    useEffect(() => { selectedIdRef.current = selectedId; }, [selectedId]);

    const canvasBgRef = useRef(canvasBg);
    useEffect(() => { canvasBgRef.current = canvasBg; }, [canvasBg]);
    const canvasSizeRef = useRef(canvasSize);
    useEffect(() => { canvasSizeRef.current = canvasSize; }, [canvasSize]);

    const getSnapshot = useCallback(() => ({
        elements: elementsRef.current,
        canvasBg: canvasBgRef.current,
        canvasSize: canvasSizeRef.current,
    }), []);

    const pushHistory = useCallback(() => {
        setPast(p => [...p, getSnapshot()]);
        setFuture([]);
    }, [getSnapshot]);

    const undo = useCallback(() => {
        setPast(p => {
            if (p.length === 0) return p;
            const snapshot = p[p.length - 1];
            setFuture(f => [getSnapshot(), ...f]);
            setElements(snapshot.elements);
            setCanvasBgState(snapshot.canvasBg);
            setCanvasSizeState(snapshot.canvasSize);
            return p.slice(0, -1);
        });
    }, [getSnapshot]);

    const redo = useCallback(() => {
        setFuture(f => {
            if (f.length === 0) return f;
            const snapshot = f[0];
            setPast(p => [...p, getSnapshot()]);
            setElements(snapshot.elements);
            setCanvasBgState(snapshot.canvasBg);
            setCanvasSizeState(snapshot.canvasSize);
            return f.slice(1);
        });
    }, [getSnapshot]);

    // Wrapped public setters that push history before changing
    const setCanvasBg = useCallback((color) => {
        pushHistory();
        setCanvasBgState(color);
    }, [pushHistory]);

    const setCanvasSize = useCallback((size) => {
        pushHistory();
        setCanvasSizeState(size);
    }, [pushHistory]);

    useEffect(() => {
        if (mode !== "model") return;
        const hasImageField = elements.some((el) => el.type === "image");
        if (hasImageField) return;
        setElements((prev) => [
            {
                id: uid(),
                type: "image",
                x: 0,
                y: 0,
                width: canvasSize.width,
                height: canvasSize.height,
                src: "",
                imageUrl: "",
                opacity: 1,
                zIndex: 0,
                fieldName: "background",
                fixed: false,
            },
            ...prev.map((el) => ({ ...el, zIndex: el.zIndex + 1 })),
        ]);
    }, [mode, elements, canvasSize]);

    const addText = useCallback(() => {
        pushHistory();
        const fieldName = mode === "model" ? `texte_${_textFieldCounter++}` : "";
        const el = {
            id: uid(),
            type: "text",
            x: 100,
            y: 100,
            width: 280,
            height: 80,
            text: mode === "model" ? "Texte exemple" : "Mon texte",
            fontSize: 28,
            fontFamily: "Arial",
            fontWeight: "normal",
            fontStyle: "normal",
            color: "#000000",
            align: "left",
            zIndex: elementsRef.current.length,
            fieldName,
            fixed: false,
        };
        setElements((p) => [...p, el]);
        setSelectedId(el.id);
    }, [mode, pushHistory]);

    const addRect = useCallback(() => {
        pushHistory();
        const el = {
            id: uid(),
            type: "rect",
            x: 150,
            y: 150,
            width: 220,
            height: 120,
            fill: "#4A90E2",
            noFill: false,
            stroke: "#2c5f9b",
            strokeWidth: 2,
            noBorder: false,
            borderRadius: 0,
            opacity: 1,
            zIndex: elementsRef.current.length,
            fieldName: "",
            fixed: false,
        };
        setElements((p) => [...p, el]);
        setSelectedId(el.id);
    }, [pushHistory]);

    const addCircle = useCallback(() => {
        pushHistory();
        const el = {
            id: uid(),
            type: "circle",
            x: 200,
            y: 200,
            width: 120,
            height: 120,
            fill: "#E24A4A",
            noFill: false,
            stroke: "#9b2c2c",
            strokeWidth: 2,
            noBorder: false,
            opacity: 1,
            zIndex: elementsRef.current.length,
            fieldName: "",
            fixed: false,
        };
        setElements((p) => [...p, el]);
        setSelectedId(el.id);
    }, [pushHistory]);

    const addImage = useCallback((src) => {
        pushHistory();
        const safeSrc = typeof src === "string" ? src : "";
        const el = {
            id: uid(),
            type: "image",
            x: 100,
            y: 100,
            width: 260,
            height: 180,
            src: safeSrc,
            imageUrl: safeSrc,
            opacity: 1,
            zIndex: elementsRef.current.length,
            fieldName: mode === "model" ? `image_${elementsRef.current.length}` : "",
            fixed: false,
        };
        setElements((p) => [...p, el]);
        setSelectedId(el.id);
    }, [mode, pushHistory]);

    const handleFileChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        pushHistory();
        const src = URL.createObjectURL(file);
        const selectedId = selectedIdRef.current;
        const selectedImage = elementsRef.current.find((el) => el.id === selectedId && el.type === "image");

        if (selectedImage) {
            setElements((prev) => prev.map((el) => (
                el.id === selectedImage.id
                    ? { ...el, src, imageUrl: "" }
                    : el
            )));
            setSelectedId(selectedImage.id);
        } else {
            const el = {
                id: uid(),
                type: "image",
                x: 100,
                y: 100,
                width: 260,
                height: 180,
                src,
                imageUrl: "",
                opacity: 1,
                zIndex: elementsRef.current.length,
                fieldName: mode === "model" ? `image_${elementsRef.current.length}` : "",
                fixed: false,
            };
            setElements((p) => [...p, el]);
            setSelectedId(el.id);
        }
        e.target.value = "";
    }, [mode, pushHistory]);

    // updateEl with leading debounce: saves the "before" snapshot once per interaction burst
    const pendingUpdateSnapshotRef = useRef(null);
    const updateElTimerRef = useRef(null);
    const updateEl = useCallback((id, props) => {
        if (!pendingUpdateSnapshotRef.current) {
            pendingUpdateSnapshotRef.current = getSnapshot();
        }
        if (updateElTimerRef.current) clearTimeout(updateElTimerRef.current);
        updateElTimerRef.current = setTimeout(() => {
            if (pendingUpdateSnapshotRef.current) {
                setPast(p => [...p, pendingUpdateSnapshotRef.current]);
                setFuture([]);
                pendingUpdateSnapshotRef.current = null;
            }
        }, 600);
        setElements((p) => p.map((e) => (e.id === id ? { ...e, ...props } : e)));
    }, [getSnapshot]);

    const deleteSelected = useCallback(() => {
        const id = selectedIdRef.current;
        if (!id) return;
        pushHistory();
        setElements((p) => p.filter((e) => e.id !== id));
        setSelectedId(null);
    }, [pushHistory]);

    const duplicateSelected = useCallback(() => {
        const id = selectedIdRef.current;
        const el = elementsRef.current.find((e) => e.id === id);
        if (!el) return;
        pushHistory();
        const newEl = {
            ...el,
            id: uid(),
            x: el.x + 20,
            y: el.y + 20,
            zIndex: elementsRef.current.length,
        };
        setElements((p) => [...p, newEl]);
        setSelectedId(newEl.id);
    }, [pushHistory]);

    const changeOrder = useCallback((dir) => {
        pushHistory();
        const id = selectedIdRef.current;
        setElements((prev) => {
            const sorted = [...prev].sort((a, b) => a.zIndex - b.zIndex);
            const idx = sorted.findIndex((e) => e.id === id);
            const swapIdx = idx + dir;
            if (idx < 0 || swapIdx < 0 || swapIdx >= sorted.length) return prev;
            const other = sorted[swapIdx];
            const myZ = sorted[idx].zIndex;
            return prev.map((e) => {
                if (e.id === id) return { ...e, zIndex: other.zIndex };
                if (e.id === other.id) return { ...e, zIndex: myZ };
                return e;
            });
        });
    }, [pushHistory]);

    useEffect(() => {
        const onKey = (e) => {
            const tag = document.activeElement.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA") return;
            if (e.key === "Delete" || e.key === "Backspace") deleteSelected();
            const mod = e.ctrlKey || e.metaKey;
            if (mod && e.key === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
            if (mod && (e.key === "y" || (e.key === "z" && e.shiftKey))) { e.preventDefault(); redo(); }
            if (mod && e.key === "d") {e.preventDefault(); duplicateSelected(); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [deleteSelected, undo, redo]);

    const handleElMouseDown = useCallback((e, id) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedId(id);
        const el = elementsRef.current.find((x) => x.id === id);
        if (el) {
            dragRef.current = {
                id,
                startX: e.clientX,
                startY: e.clientY,
                elX: el.x,
                elY: el.y,
                hasMoved: false,
                snapshot: getSnapshot(),
            };
        }
    }, [getSnapshot]);

    const handleResizeMouseDown = useCallback((e, id) => {
        e.preventDefault();
        e.stopPropagation();
        const el = elementsRef.current.find((x) => x.id === id);
        if (el) {
            resizeRef.current = {
                id,
                startX: e.clientX,
                startY: e.clientY,
                elW: el.width,
                elH: el.height,
                hasMoved: false,
                snapshot: getSnapshot(),
            };
        }
    }, [getSnapshot]);

    useEffect(() => {
        const onMove = (e) => {
            const s = scaleRef.current;
            if (dragRef.current) {
                if (!dragRef.current.hasMoved) {
                    const snap = dragRef.current.snapshot;
                    setPast(p => [...p, snap]);
                    setFuture([]);
                    dragRef.current.hasMoved = true;
                }
                const { id, startX, startY, elX, elY } = dragRef.current;
                setElements((p) => p.map((el) =>
                    el.id === id
                        ? { ...el, x: elX + (e.clientX - startX) / s, y: elY + (e.clientY - startY) / s }
                        : el
                ));
            }
            if (resizeRef.current) {
                if (!resizeRef.current.hasMoved) {
                    const snap = resizeRef.current.snapshot;
                    setPast(p => [...p, snap]);
                    setFuture([]);
                    resizeRef.current.hasMoved = true;
                }
                const { id, startX, startY, elW, elH } = resizeRef.current;
                setElements((p) => p.map((el) =>
                    el.id === id
                        ? {
                            ...el,
                            width: Math.max(20, elW + (e.clientX - startX) / s),
                            height: Math.max(20, elH + (e.clientY - startY) / s),
                        }
                        : el
                ));
            }
        };
        const onUp = () => {
            dragRef.current = null;
            resizeRef.current = null;
        };
        window.addEventListener("mousemove", onMove);
        window.addEventListener("mouseup", onUp);
        return () => {
            window.removeEventListener("mousemove", onMove);
            window.removeEventListener("mouseup", onUp);
        };
    }, []);

    return {
        title,
        setTitle,
        elements,
        setElements,
        selectedId,
        setSelectedId,
        selectedEl: elements.find((e) => e.id === selectedId) ?? null,
        canvasBg,
        setCanvasBg,
        canvasSize,
        setCanvasSize,
        scaleRef,
        fileInputRef,
        addText,
        addRect,
        addCircle,
        addImage,
        handleFileChange,
        updateEl,
        deleteSelected,
        duplicateSelected,
        changeOrder,
        handleElMouseDown,
        handleResizeMouseDown,
        undo,
        redo,
        canUndo,
        canRedo,
    };
}
